/* ==========================================================================
   ps-fx.js — Peakspan H2 motion/effects layer (peakspanh2.com)
   Companion to assets/ps-fx.css. Static site, no external requests, no
   dependencies, ES5 only, idempotent, additive. Copy is never rewritten:
   the count-up restores the element's ORIGINAL text byte-for-byte when it
   finishes, so frozen content stays frozen.

   Owns (SPEC-ROUND4 sections A + C):
     1 · Ambient micro-bubble canvas, sitewide, self-mounting.
         Fixed, pointer-events:none, DPR-aware, rAF, ~40 desktop / ~18 mobile,
         pauses on hidden tab, renders nothing under prefers-reduced-motion,
         subtle cursor attraction on fine-pointer devices only.
     2 · The flowing-water ticker is pure CSS (ps-fx.css) — nothing to do here
         beyond honouring the reduced-motion contract.
     3 · Count-up numerals: [data-ps-count]
     4 · Scroll reveals with stagger: [data-ps-reveal]
     5 · Scroll-progress rail with a green pulse dot (self-mounting).

   The only global is `window.psFx`.
   ========================================================================== */
(function () {
  'use strict';

  var W = window;
  var D = document;

  /* ---- single-instance guard ------------------------------------------ */
  var psFx = W.psFx || (W.psFx = {});
  if (psFx.__booted) return;
  psFx.__booted = true;
  psFx.version = '1.0.0';

  /* ---- opt-out: <html data-ps-fx="off"> kills the whole layer ---------- */
  var root = D.documentElement;
  if (root.getAttribute('data-ps-fx') === 'off') {
    psFx.disabled = true;
    psFx.scan = function () {};
    psFx.refresh = function () {};
    psFx.destroy = function () {};
    return;
  }

  var TAU = Math.PI * 2;

  function mq(q) {
    try { return W.matchMedia ? W.matchMedia(q) : null; } catch (e) { return null; }
  }
  function mqOn(m, fn) {
    if (!m) return;
    if (m.addEventListener) m.addEventListener('change', fn);
    else if (m.addListener) m.addListener(fn);
  }

  var reduceMQ = mq('(prefers-reduced-motion: reduce)');
  function reduced() { return !!(reduceMQ && reduceMQ.matches); }

  var finePointerMQ = mq('(hover: hover) and (pointer: fine)');
  var mobileMQ = mq('(max-width: 749px)');

  var hasIO = 'IntersectionObserver' in W;
  var raf = W.requestAnimationFrame ? W.requestAnimationFrame.bind(W) : null;
  var caf = W.cancelAnimationFrame ? W.cancelAnimationFrame.bind(W) : null;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function optOff(el, attr) { return el && el.getAttribute(attr) === 'off'; }
  function num(v, dflt) {
    var n = parseFloat(v);
    return isFinite(n) ? n : dflt;
  }

  /* ======================================================================
     1 · AMBIENT MICRO-BUBBLES
     ====================================================================== */

  var bubbles = (function () {
    var api = { mounted: false, running: false };
    var canvas = null, ctx = null, sprite = null;
    var list = [], w = 0, h = 0, dpr = 1;
    var frame = 0, last = 0;
    var pointer = { x: -9999, y: -9999, active: false };
    var attract = false;
    var resizeTimer = 0;

    /* one pre-rendered glassy bubble, drawn scaled per particle — far
       cheaper than building a radial gradient for every bubble every frame.
       It carries both a bright top rim and a dark bottom rim so it reads on
       the light platinum sheet AND on the dark ink sections. */
    function makeSprite() {
      var S = 96;
      var c = D.createElement('canvas');
      c.width = S; c.height = S;
      var g = c.getContext('2d');
      if (!g) return null;
      var cx = S / 2, cy = S / 2, r = S / 2 - 6;

      var rg = g.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.08, cx, cy, r);
      rg.addColorStop(0, 'rgba(255,255,255,0.22)');
      rg.addColorStop(0.55, 'rgba(199,204,212,0.05)');
      rg.addColorStop(1, 'rgba(127,182,217,0.16)');
      g.fillStyle = rg;
      g.beginPath(); g.arc(cx, cy, r, 0, TAU); g.fill();

      g.lineWidth = S * 0.034;
      /* lower arc: a touch of ink so the bubble survives a light background */
      g.strokeStyle = 'rgba(20,24,30,0.20)';
      g.beginPath(); g.arc(cx, cy, r, TAU * 0.02, TAU * 0.46); g.stroke();
      /* upper arc: bright rim so it survives a dark background */
      g.strokeStyle = 'rgba(255,255,255,0.82)';
      g.beginPath(); g.arc(cx, cy, r, TAU * 0.52, TAU * 0.96); g.stroke();
      /* specular */
      g.fillStyle = 'rgba(255,255,255,0.88)';
      g.beginPath(); g.arc(cx - r * 0.34, cy - r * 0.38, r * 0.15, 0, TAU); g.fill();

      return c;
    }

    function density() {
      return (mobileMQ && mobileMQ.matches) ? 18 : 40;
    }

    function spawn(b, seeded) {
      b.r = 1.6 + Math.random() * 4.6;
      b.x = Math.random() * w;
      b.y = seeded ? Math.random() * h : h + b.r + Math.random() * h * 0.35;
      b.sp = 9 + Math.random() * 20 + b.r * 1.6;   /* px/s upward */
      b.amp = 4 + Math.random() * 13;              /* px/s lateral sway */
      b.freq = 0.55 + Math.random() * 1.6;         /* rad/s sway rate */
      b.phase = Math.random() * TAU;
      b.a = 0.20 + Math.random() * 0.38;
      b.vx = 0; b.vy = 0;
      return b;
    }

    function build() {
      var want = density();
      list.length = 0;
      for (var i = 0; i < want; i++) list.push(spawn({}, true));
    }

    function size() {
      if (!canvas) return;
      dpr = Math.min(2, W.devicePixelRatio || 1);
      w = Math.max(1, root.clientWidth || W.innerWidth || 1);
      h = Math.max(1, W.innerHeight || root.clientHeight || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function step(ts) {
      frame = 0;
      if (!api.running) return;
      if (!last) last = ts;
      var dt = (ts - last) / 1000;
      last = ts;
      if (dt > 0.05) dt = 0.05;

      ctx.clearRect(0, 0, w, h);

      var damp = Math.exp(-3 * dt);
      var R = 190, R2 = R * R, i, b;

      for (i = 0; i < list.length; i++) {
        b = list[i];

        if (attract && pointer.active) {
          var dx = pointer.x - b.x, dy = pointer.y - b.y;
          var d2 = dx * dx + dy * dy;
          if (d2 < R2 && d2 > 1) {
            var d = Math.sqrt(d2);
            var pull = (1 - d / R) * 30;           /* px/s^2, deliberately gentle */
            b.vx += (dx / d) * pull * dt;
            b.vy += (dy / d) * pull * dt;
          }
        }
        b.vx *= damp; b.vy *= damp;
        if (b.vx > 40) b.vx = 40; else if (b.vx < -40) b.vx = -40;
        if (b.vy > 40) b.vy = 40; else if (b.vy < -40) b.vy = -40;

        b.phase += b.freq * dt;
        b.x += (Math.sin(b.phase) * b.amp + b.vx) * dt;
        b.y -= (b.sp - b.vy) * dt;

        if (b.x < -20) b.x = w + 20;
        else if (b.x > w + 20) b.x = -20;
        if (b.y + b.r < -12) spawn(b, false);

        ctx.globalAlpha = b.a;
        ctx.drawImage(sprite, b.x - b.r, b.y - b.r, b.r * 2, b.r * 2);
      }
      ctx.globalAlpha = 1;

      frame = raf(step);
    }

    function start() {
      if (api.running || !api.mounted || reduced() || !raf) return;
      api.running = true;
      last = 0;
      frame = raf(step);
    }

    function stop() {
      api.running = false;
      if (frame && caf) caf(frame);
      frame = 0;
    }

    function onResize() {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        resizeTimer = 0;
        var pw = w, ph = h;
        size();
        for (var i = 0; i < list.length; i++) {
          list[i].x = pw ? list[i].x * (w / pw) : Math.random() * w;
          list[i].y = ph ? list[i].y * (h / ph) : Math.random() * h;
        }
        if (list.length !== density()) build();
        if (!api.running) { /* repaint one frame so a resized paused layer is not stale */
          ctx.clearRect(0, 0, w, h);
        }
      }, 160);
    }

    function onPointer(e) {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.active = true;
    }
    function onPointerOut(e) {
      /* only when the pointer actually leaves the document, not on every
         element-to-element mouseout inside the page */
      if (e && e.type === 'mouseout' && (e.relatedTarget || e.toElement)) return;
      pointer.active = false;
    }

    api.mount = function () {
      if (api.mounted || !D.body) return;
      if (optOff(root, 'data-ps-fx-bubbles')) return;
      if (reduced()) return;                       /* renders nothing */
      canvas = D.createElement('canvas');
      canvas.className = 'psfx-bubbles';
      canvas.setAttribute('aria-hidden', 'true');
      ctx = canvas.getContext ? canvas.getContext('2d', { alpha: true }) : null;
      if (!ctx || !raf) { canvas = null; return; }
      sprite = makeSprite();
      if (!sprite) { canvas = null; ctx = null; return; }

      D.body.insertBefore(canvas, D.body.firstChild);
      api.mounted = true;
      size();
      build();

      /* desktop only: a real fine pointer AND not a phone-width viewport */
      attract = !!(finePointerMQ && finePointerMQ.matches) && !(mobileMQ && mobileMQ.matches);
      if (attract) {
        W.addEventListener('mousemove', onPointer, { passive: true });
        W.addEventListener('mouseout', onPointerOut, { passive: true });
        W.addEventListener('blur', onPointerOut, { passive: true });
      }
      W.addEventListener('resize', onResize, { passive: true });
      W.addEventListener('orientationchange', onResize, { passive: true });
      start();
    };

    api.unmount = function () {
      stop();
      if (attract) {
        W.removeEventListener('mousemove', onPointer);
        W.removeEventListener('mouseout', onPointerOut);
        W.removeEventListener('blur', onPointerOut);
      }
      W.removeEventListener('resize', onResize);
      W.removeEventListener('orientationchange', onResize);
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = null; ctx = null; sprite = null; list.length = 0;
      api.mounted = false;
    };

    api.pause = stop;
    api.resume = start;
    /* read-only QA surface — used by the deploy click-test, not by the site */
    api.stats = function () {
      return { count: list.length, w: w, h: h, dpr: dpr, attract: attract, running: api.running };
    };
    return api;
  })();

  D.addEventListener('visibilitychange', function () {
    if (D.hidden) bubbles.pause();
    else bubbles.resume();
  });

  mqOn(reduceMQ, function () {
    if (reduced()) bubbles.unmount();
    else bubbles.mount();
    scan(D);
  });

  /* ======================================================================
     3 · COUNT-UP NUMERALS — [data-ps-count]
     The element's own text is the source of truth. We parse the numeral out
     of it, animate 0 -> value, and on the last frame put the ORIGINAL string
     back verbatim. Copy therefore cannot drift.
     ====================================================================== */

  function group(intPart) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function parseCount(el) {
    var finalText = el.textContent;
    if (finalText === null || finalText === undefined) return null;

    /* prefix / numeral / suffix straight out of the rendered text */
    var m = String(finalText).match(/\d[\d.,\u00A0\u202F ]*\d|\d/);
    if (!m) return null;
    var numeral = m[0];
    var idx = m.index;

    /* an explicit data-ps-count="8000" overrides the parsed target */
    var attr = el.getAttribute('data-ps-count');
    var target;
    if (attr && /\d/.test(attr)) target = parseFloat(String(attr).replace(/[,\s\u00A0\u202F]/g, ''));
    if (!isFinite(target)) target = parseFloat(numeral.replace(/[,\s\u00A0\u202F]/g, ''));
    if (!isFinite(target)) return null;

    var dot = numeral.lastIndexOf('.');
    var decimals = num(el.getAttribute('data-ps-count-decimals'),
      dot === -1 ? 0 : (numeral.length - dot - 1));
    if (decimals < 0 || decimals > 6) decimals = 0;

    var grouped = el.getAttribute('data-ps-count-group');
    grouped = grouped === null ? numeral.indexOf(',') !== -1 : grouped !== '0';

    return {
      el: el,
      finalText: finalText,
      prefix: String(finalText).slice(0, idx),
      suffix: String(finalText).slice(idx + numeral.length),
      target: target,
      decimals: decimals,
      grouped: grouped,
      from: num(el.getAttribute('data-ps-count-from'), 0),
      duration: Math.max(160, num(el.getAttribute('data-ps-count-duration'), 1400))
    };
  }

  function render(spec, value) {
    var s = value.toFixed(spec.decimals);
    if (spec.grouped) {
      var parts = s.split('.');
      parts[0] = group(parts[0]);
      s = parts.join('.');
    }
    spec.el.textContent = spec.prefix + s + spec.suffix;
  }

  function runCount(spec) {
    var el = spec.el;

    /* pin the box so shorter intermediate values cannot reflow the line */
    var pinned = false;
    try {
      var cs = W.getComputedStyle(el);
      if (cs && cs.display === 'inline') {
        var wpx = el.getBoundingClientRect().width;
        if (wpx > 0) {
          el.style.display = 'inline-block';
          el.style.minWidth = wpx.toFixed(2) + 'px';
          pinned = true;
        }
      }
    } catch (e) { /* non-fatal */ }

    var t0 = 0;
    function tick(ts) {
      if (!t0) t0 = ts;
      var t = (ts - t0) / spec.duration;
      if (t > 1) t = 1;
      var e = 1 - Math.pow(1 - t, 3);   /* easeOutCubic — matches ps-chapters.js */
      if (t < 1) {
        render(spec, spec.from + (spec.target - spec.from) * e);
        raf(tick);
      } else {
        el.textContent = spec.finalText;               /* byte-identical */
        if (pinned) { el.style.display = ''; el.style.minWidth = ''; }
      }
    }
    render(spec, spec.from);
    raf(tick);
  }

  var countIO = null;
  function countObserver() {
    if (countIO || !hasIO) return countIO;
    countIO = new IntersectionObserver(function (entries) {
      each(entries, function (en) {
        if (!en.isIntersecting) return;
        countIO.unobserve(en.target);
        var spec = en.target.__psfxCount;
        if (spec) { en.target.__psfxCount = null; runCount(spec); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.2 });
    return countIO;
  }

  function initCounts(scope) {
    var nodes = (scope || D).querySelectorAll('[data-ps-count]');
    /* reduced motion, no IO, no rAF: leave every final value exactly as
       authored. Nothing is marked bound, so if the user turns reduced motion
       off later a rescan can still pick these up. */
    if (reduced() || !hasIO || !raf) return;
    each(nodes, function (el) {
      if (el.__psfxCountBound) return;
      el.__psfxCountBound = true;
      var spec = parseCount(el);
      if (!spec) return;
      el.__psfxCount = spec;
      countObserver().observe(el);
    });
  }

  /* ======================================================================
     4 · SCROLL REVEALS — [data-ps-reveal] with automatic stagger
     ====================================================================== */

  var revealIO = null;

  function staggerFor(el) {
    var explicit = el.getAttribute('data-ps-reveal-delay');
    if (explicit !== null && explicit !== '') return Math.max(0, num(explicit, 0));

    var groupEl = el.parentElement;
    if (el.closest) groupEl = el.closest('[data-ps-reveal-group]') || el.parentElement;
    if (!groupEl) return 0;

    var stepMs = num(groupEl.getAttribute && groupEl.getAttribute('data-ps-reveal-stagger'), 80);
    var sibs = groupEl.querySelectorAll('[data-ps-reveal]');
    var i = Array.prototype.indexOf.call(sibs, el);
    if (i < 0) i = 0;
    if (i > 7) i = 7;                     /* cap the cascade at ~560 ms */
    return i * stepMs;
  }

  function revealObserver() {
    if (revealIO || !hasIO) return revealIO;
    revealIO = new IntersectionObserver(function (entries) {
      each(entries, function (en) {
        if (!en.isIntersecting) return;
        revealIO.unobserve(en.target);
        en.target.classList.add('psfx-in');
      });
    }, { rootMargin: '0px 0px -4% 0px', threshold: 0 }); /* hotfix 2026-08-27: threshold 0.08 never fired for sections taller than ~12 viewports (documents wall stayed invisible) */
    return revealIO;
  }

  function initReveals(scope) {
    var nodes = (scope || D).querySelectorAll('[data-ps-reveal]');
    if (!nodes.length) return;

    if (reduced() || !hasIO) {
      each(nodes, function (el) { el.classList.add('psfx-in'); });
      root.classList.remove('psfx-reveal-on');
      return;
    }
    root.classList.add('psfx-reveal-on');
    each(nodes, function (el) {
      if (el.__psfxRevealBound) return;
      el.__psfxRevealBound = true;
      var d = staggerFor(el);
      if (d) el.style.setProperty('--psfx-d', d + 'ms');
      revealObserver().observe(el);
    });
  }

  /* ======================================================================
     5 · SCROLL-PROGRESS RAIL
     ====================================================================== */

  var rail = (function () {
    var api = { mounted: false };
    var el = null, fill = null, dot = null;
    var width = 0, dir = 1, ticking = false;

    function measure() {
      width = el ? el.getBoundingClientRect().width : 0;
    }

    function paint() {
      ticking = false;
      if (!el) return;
      var max = (root.scrollHeight || 0) - (W.innerHeight || 0);
      var y = W.pageYOffset || root.scrollTop || 0;
      var p = max > 0 ? y / max : 0;
      if (p < 0) p = 0; else if (p > 1) p = 1;
      fill.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      dot.style.transform = 'translate3d(' + (p * width * dir).toFixed(2) + 'px,0,0)';
    }

    function request() {
      if (ticking || !raf) { if (!raf) paint(); return; }
      ticking = true;
      raf(paint);
    }

    function onResize() { measure(); request(); }

    api.mount = function () {
      if (api.mounted || !D.body) return;
      if (optOff(root, 'data-ps-fx-rail')) return;
      el = D.createElement('div');
      el.className = 'psfx-rail';
      el.setAttribute('aria-hidden', 'true');
      fill = D.createElement('i');
      fill.className = 'psfx-rail__fill';
      dot = D.createElement('i');
      dot.className = 'psfx-rail__dot';
      el.appendChild(fill);
      el.appendChild(dot);
      D.body.appendChild(el);
      api.mounted = true;

      try {
        if (W.getComputedStyle(root).direction === 'rtl') { dir = -1; el.classList.add('psfx-rail--rtl'); }
      } catch (e) { /* default ltr */ }

      measure();
      paint();
      W.addEventListener('scroll', request, { passive: true });
      W.addEventListener('resize', onResize, { passive: true });
      W.addEventListener('orientationchange', onResize, { passive: true });
      W.addEventListener('load', onResize, { passive: true });
    };

    api.refresh = function () { measure(); request(); };

    api.unmount = function () {
      W.removeEventListener('scroll', request);
      W.removeEventListener('resize', onResize);
      W.removeEventListener('orientationchange', onResize);
      W.removeEventListener('load', onResize);
      if (el && el.parentNode) el.parentNode.removeChild(el);
      el = fill = dot = null;
      api.mounted = false;
    };

    return api;
  })();

  /* ======================================================================
     BOOT + public surface
     ====================================================================== */

  function scan(scopeEl) {
    var scope = scopeEl && scopeEl.querySelectorAll ? scopeEl : D;
    initCounts(scope);
    initReveals(scope);
  }

  function boot() {
    if (!D.body) return;
    bubbles.mount();
    rail.mount();
    scan(D);
    watch();
  }

  /* Other agents inject DOM (popups, "last seen" strip, buy-bar). Pick up
     anything they add without making them remember to call scan(). Cheap:
     debounced, and mutations caused by our own count-up are ignored. */
  var watching = false, watchTimer = 0;
  function watch() {
    if (watching || !W.MutationObserver) return;
    watching = true;
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (t && t.closest && t.closest('[data-ps-count]')) continue;
        if (!records[i].addedNodes || !records[i].addedNodes.length) continue;
        if (watchTimer) clearTimeout(watchTimer);
        watchTimer = setTimeout(function () { watchTimer = 0; scan(D); rail.refresh(); }, 150);
        return;
      }
    });
    mo.observe(D.body, { childList: true, subtree: true });
    psFx.__mo = mo;
  }

  psFx.scan = scan;
  psFx.refresh = function () { scan(D); rail.refresh(); };
  psFx.bubbles = bubbles;
  psFx.rail = rail;
  psFx.reducedMotion = reduced;
  psFx.destroy = function () {
    bubbles.unmount();
    rail.unmount();
    if (psFx.__mo) { psFx.__mo.disconnect(); psFx.__mo = null; watching = false; }
    if (countIO) { countIO.disconnect(); countIO = null; }
    if (revealIO) { revealIO.disconnect(); revealIO = null; }
    root.classList.remove('psfx-reveal-on');
  };

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
